import { useEffect, useState } from 'react';
import { FormControl, FormControlLabel, FormLabel, Grid, Radio, RadioGroup, TextField, IconButton, Typography, Paper, Divider, Button, Dialog, DialogTitle, DialogContent, DialogActions, Switch, MenuItem, Snackbar, Alert } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import { handleChangeContractIsActiveAction, handleChangeContractIsInspectionAction, handleChangeContractNameAction, handleChangeContractReportTitleAction, handleSetContractZoneListAction, handleChangeContractZoneFieldAction, handleAddImageTypeAction, handleUpdateImageTypeAction, handleMoveImageTypeAction, handleSetImageTypeListAction } from '../../redux/contract/action';
import { ContractZone } from '../../redux/contract/state';
import { IRootState } from '../../redux/store';
import { axiosGet } from '../../util/axios';
import { axiosAPIWithJson } from '../../util/axios';
import { ButtonTextGrid } from '../..';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AddIcon from '@mui/icons-material/Add';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

interface ZoneBinItem {
    bin_id: number;
    code: string;
    location_cn: string;
    toilet_number: string;
    status: number;
    report_zone_sequence: number;
}

const MAX_IMAGE_TYPE_SELECTION = 5;

function sanitizeImageTypeNameEn(value: string): string {
    return value.replace(/\s+/g, '');
}

function NewContractForm() {
    const dispatch = useDispatch()
    const [openZoneDetail, setOpenZoneDetail] = useState(false);
    const [editingZoneIndex, setEditingZoneIndex] = useState<number>(-1);
    const [draftZone, setDraftZone] = useState<ContractZone | null>(null);
    const [zoneBinList, setZoneBinList] = useState<ZoneBinItem[]>([]);
    const [selectedNewContractId, setSelectedNewContractId] = useState<string>('');
    const [moveZoneSnackbar, setMoveZoneSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

    // Image type form state
    const [newTypeName, setNewTypeName] = useState('');
    const [newTypeNameEn, setNewTypeNameEn] = useState('');
    const [newTypeReportColumn, setNewTypeReportColumn] = useState('');
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editTypeName, setEditTypeName] = useState('');
    const [editingReportColumnIndex, setEditingReportColumnIndex] = useState<number | null>(null);
    const [editReportColumnName, setEditReportColumnName] = useState('');

    const {
        Contract
    } = useSelector((state: IRootState) => state.contractState);
    const { contractList } = useSelector((state: IRootState) => state.staticInfoState);

    const isEditing = !!Contract.contract_id;
    const imageTypeList = Contract.imageTypeList || [];

    useEffect(() => {
        if (isEditing && Contract.contract_id) {
            fetchExistingImageTypes(Contract.contract_id);
            fetchZoneList(Contract.contract_id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Contract.contract_id]);

    async function fetchExistingImageTypes(contractId: string) {
        try {
            const response = await axiosGet(`/api/cleaning/imageType?contract_id=${contractId}`);
            if (response.data.success) {
                const types = response.data.imageTypleList.map((t: any) => ({
                    cleaning_image_type_id: t.cleaning_image_type_id,
                    image_type_name: t.image_type_name,
                    image_type_name_en: t.image_type_name_en,
                    report_column_name: t.report_column_name || '',
                    is_active: t.is_active ?? 1,
                }));
                dispatch(handleSetImageTypeListAction(types));
            }
        } catch (error) {
            console.error('Failed to fetch image types for contract', error);
        }
    }

    async function fetchZoneList(contractId: string) {
        try {
            const response = await axiosGet(`/api/contract/zone/stats?contractId=${contractId}`);
            if (response.data.success) {
                const zones: ContractZone[] = (response.data.zoneStats || []).map((z: any) => ({
                    zone_id: z.zone_id,
                    zone_name: z.zone_name || '',
                    report_zone_name: z.report_zone_name || '',
                    contact_person: z.contact_person || '',
                    tel: z.tel || '',
                    email: z.email || '',
                    contract_report_sequence: z.contract_report_sequence ?? undefined,
                    bin_count: z.bin_count ?? 0,
                    contract_id: z.contract_id,
                }));
                dispatch(handleSetContractZoneListAction(zones));
            }
        } catch (error) {
            console.error('Failed to fetch zone list', error);
        }
    }

    function handleChangeContractName(event: React.ChangeEvent<HTMLInputElement>) {
        const contractName = event.target.value;
        dispatch(handleChangeContractNameAction(contractName))
    }

    function handleChangeIsActive(event: React.ChangeEvent<HTMLInputElement>) {
        const isActive = event.target.value;
        dispatch(handleChangeContractIsActiveAction(isActive))
    }

    function handleChangeReportTitle(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        dispatch(handleChangeContractReportTitleAction(event.target.value))
    }

    // --- Image Type Actions ---

    function handleAddImageType() {
        if (!newTypeName.trim() || !newTypeNameEn.trim() || !newTypeReportColumn.trim()) return;
        if (imageTypeList.length >= MAX_IMAGE_TYPE_SELECTION) return;

        dispatch(handleAddImageTypeAction({
            image_type_name: newTypeName.trim(),
            image_type_name_en: sanitizeImageTypeNameEn(newTypeNameEn.trim()),
            report_column_name: newTypeReportColumn.trim(),
            is_active: 1,
        }));
        setNewTypeName('');
        setNewTypeNameEn('');
        setNewTypeReportColumn('');
    }

    function handleToggleImageTypeActive(index: number) {
        const item = imageTypeList[index];
        dispatch(handleUpdateImageTypeAction(index, {
            ...item,
            is_active: item.is_active === 1 ? 0 : 1,
        }));
    }

    function handleStartEditTypeName(index: number) {
        setEditingIndex(index);
        setEditTypeName(imageTypeList[index].image_type_name);
    }

    function handleConfirmEditTypeName(index: number) {
        if (!editTypeName.trim()) {
            setEditingIndex(null);
            return;
        }
        const item = imageTypeList[index];
        dispatch(handleUpdateImageTypeAction(index, {
            ...item,
            image_type_name: editTypeName.trim(),
        }));
        setEditingIndex(null);
    }

    function handleStartEditReportColumn(index: number) {
        setEditingReportColumnIndex(index);
        setEditReportColumnName(imageTypeList[index].report_column_name);
    }

    function handleConfirmEditReportColumn(index: number) {
        if (!editReportColumnName.trim()) {
            setEditingReportColumnIndex(null);
            return;
        }
        const item = imageTypeList[index];
        dispatch(handleUpdateImageTypeAction(index, {
            ...item,
            report_column_name: editReportColumnName.trim(),
        }));
        setEditingReportColumnIndex(null);
    }

    function handleMoveImageType(index: number, direction: 'up' | 'down') {
        dispatch(handleMoveImageTypeAction(index, direction));
    }

    // --- Zone Actions ---

    function handleDraftZoneField(field: keyof ContractZone, value: string | number) {
        setDraftZone((prev) => (prev ? { ...prev, [field]: value } : prev));
    }

    async function handleOpenZoneDetail(index: number) {
        setEditingZoneIndex(index);
        setOpenZoneDetail(true);
        setZoneBinList([]);
        setSelectedNewContractId('');

        const zone = Contract.zoneList?.[index];
        if (zone) {
            setDraftZone({ ...zone });
            setSelectedNewContractId(Contract.contract_id || '');
        }
        if (zone?.zone_id) {
            try {
                const response = await axiosGet(`/api/contract/zone/${zone.zone_id}/bins`);
                if (response.data.success) {
                    setZoneBinList(response.data.binList || []);
                }
            } catch (error) {
                console.error('Failed to fetch zone bin list', error);
            }
        }
    }

    function handleCloseZoneDetail() {
        setOpenZoneDetail(false);
        setEditingZoneIndex(-1);
        setDraftZone(null);
        setZoneBinList([]);
        setSelectedNewContractId('');
    }

    async function handleMoveZoneToContract() {
        if (!draftZone?.zone_id || !selectedNewContractId) return;

        try {
            const response = await axiosAPIWithJson.put(`/api/contract/zone/${draftZone.zone_id}`, {
                contract_id: selectedNewContractId,
            });
            if (response.data.success) {
                setMoveZoneSnackbar({ open: true, message: response.data.message, severity: 'success' });
                // Refresh zone list for current contract
                if (Contract.contract_id) {
                    await fetchZoneList(Contract.contract_id);
                }
                handleCloseZoneDetail();
            } else {
                setMoveZoneSnackbar({ open: true, message: response.data.message, severity: 'error' });
            }
        } catch (error) {
            setMoveZoneSnackbar({ open: true, message: '搬移區份失敗', severity: 'error' });
        }
    }

    function handleConfirmZoneDetail() {
        if (editingZoneIndex >= 0 && draftZone) {
            const fields: (keyof ContractZone)[] = [
                'zone_name',
                'report_zone_name',
                'contact_person',
                'tel',
                'email',
            ];
            fields.forEach((field) => {
                dispatch(handleChangeContractZoneFieldAction(
                    editingZoneIndex,
                    field,
                    draftZone[field] ?? '',
                ));
            });
        }
        handleCloseZoneDetail();
    }

    const zoneListLength = Contract.zoneList?.length ?? 0;

    // --- Image Type DataGrid ---

    const imageTypeColumns: GridColDef[] = [
        {
            field: 'sort',
            headerName: '排序',
            flex: 0.6,
            sortable: false,
            renderCell: (params) => (
                <Grid container alignItems="center">
                    <IconButton
                        size="small"
                        onClick={() => handleMoveImageType(params.row._index, 'up')}
                        disabled={params.row._index === 0}
                    >
                        <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => handleMoveImageType(params.row._index, 'down')}
                        disabled={params.row._index === imageTypeList.length - 1}
                    >
                        <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                </Grid>
            ),
        },
        {
            field: 'image_type_name',
            headerName: '中文名稱',
            flex: 2,
            renderCell: (params) => {
                const idx = params.row._index;
                if (editingIndex === idx) {
                    return (
                        <TextField
                            size="small"
                            value={editTypeName}
                            onChange={(e) => setEditTypeName(e.target.value)}
                            onBlur={() => handleConfirmEditTypeName(idx)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleConfirmEditTypeName(idx);
                                if (e.key === 'Escape') setEditingIndex(null);
                            }}
                            autoFocus
                            fullWidth
                            variant="standard"
                        />
                    );
                }
                return (
                    <Typography
                        variant="body2"
                        style={{ cursor: isEditing ? 'pointer' : 'default', lineHeight: '2.5' }}
                        onClick={() => isEditing && handleStartEditTypeName(idx)}
                    >
                        {params.value}
                        {isEditing && <EditNoteIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle', opacity: 0.5 }} />}
                    </Typography>
                );
            },
        },
        {
            field: 'image_type_name_en',
            headerName: '英文名稱',
            flex: 2,
        },
        {
            field: 'report_column_name',
            headerName: '報告欄位名稱',
            flex: 2,
            renderCell: (params) => {
                const idx = params.row._index;
                if (editingReportColumnIndex === idx) {
                    return (
                        <TextField
                            size="small"
                            value={editReportColumnName}
                            onChange={(e) => setEditReportColumnName(e.target.value)}
                            onBlur={() => handleConfirmEditReportColumn(idx)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleConfirmEditReportColumn(idx);
                                if (e.key === 'Escape') setEditingReportColumnIndex(null);
                            }}
                            autoFocus
                            fullWidth
                            variant="standard"
                        />
                    );
                }
                return (
                    <Typography
                        variant="body2"
                        style={{ cursor: isEditing ? 'pointer' : 'default', lineHeight: '2.5' }}
                        onClick={() => isEditing && handleStartEditReportColumn(idx)}
                    >
                        {params.value}
                        {isEditing && <EditNoteIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle', opacity: 0.5 }} />}
                    </Typography>
                );
            },
        },
        {
            field: 'is_active',
            headerName: '生效',
            flex: 0.8,
            renderCell: (params) => {
                const idx = params.row._index;
                const item = imageTypeList[idx];
                const isPersisted = !!item?.cleaning_image_type_id;
                return (
                    <Switch
                        size="small"
                        checked={item?.is_active !== 0}
                        disabled={!isEditing && false}
                        onChange={() => handleToggleImageTypeActive(idx)}
                        color="success"
                    />
                );
            },
        },
    ];

    const imageTypeRows = imageTypeList.map((type, index) => ({
        id: type.cleaning_image_type_id ?? `new-${index}`,
        _index: index,
        image_type_name: type.image_type_name,
        image_type_name_en: type.image_type_name_en,
        report_column_name: type.report_column_name,
        is_active: type.is_active,
    }));

    // --- Zone DataGrid ---

    const zoneColumns: GridColDef[] = [
        { field: 'zone_name', headerName: '區份名稱', flex: 2 },
        { field: 'report_zone_name', headerName: '報告區份名稱', flex: 2 },
        { field: 'bin_count', headerName: '廁所數量', flex: 1, type: 'number' },
        { field: 'contact_person', headerName: '聯絡人', flex: 1.5 },
        { field: 'tel', headerName: '電話', flex: 1 },
        { field: 'email', headerName: '電郵', flex: 1.5 },
        {
            field: 'detail',
            headerName: '操作',
            flex: 1,
            renderCell: (params) => (
                <Button
                    size="small"
                    startIcon={<EditNoteIcon sx={{ color: 'white' }} />}
                    onClick={() => handleOpenZoneDetail(params.row._index)}
                >
                    <ButtonTextGrid>詳情</ButtonTextGrid>
                </Button>
            ),
        },
    ];

    const zoneRows = (Contract.zoneList || []).map((zone, index) => ({
        id: zone.zone_id ?? `new-${index}`,
        _index: index,
        zone_name: zone.zone_name || '',
        report_zone_name: zone.report_zone_name || '',
        bin_count: zone.bin_count ?? 0,
        contact_person: zone.contact_person || '',
        tel: zone.tel || '',
        email: zone.email || '',
    }));

    const binColumns: GridColDef[] = [
        { field: 'code', headerName: '參考編號', flex: 1 },
        { field: 'location_cn', headerName: '廁所名稱', flex: 2 },
        { field: 'toilet_number', headerName: '廁所編號', flex: 1 },
        {
            field: 'status',
            headerName: '狀況',
            flex: 1,
            valueFormatter: (params) => params.value === 1 ? '生效' : '已關閉',
        },
    ];

    const binRows = zoneBinList.map((bin) => ({
        id: bin.bin_id,
        code: bin.code || '',
        location_cn: bin.location_cn || '',
        toilet_number: bin.toilet_number || '',
        status: bin.status,
    }));

    const editingZone = draftZone;

    // Determine if contract has changed in zone detail dialog
    const currentContractId = Contract.contract_id || '';
    const contractChanged = selectedNewContractId !== '' && selectedNewContractId !== currentContractId;

    return (
        <Grid padding={1} style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>
            <Grid style={{ flex: 1 }}>
                <TextField
                    id="outlined-required"
                    placeholder="請輸入合約名稱"
                    value={Contract.contract}
                    fullWidth
                    label="合約名稱"
                    required
                    onChange={handleChangeContractName}
                />
            </Grid>

            <Grid style={{ flex: 1 }}>
                <TextField
                    placeholder="例如：Provision of Hiring and Servicing of Enhanced Type Portable Toilets\nFor Country Parks in ..."
                    value={Contract.report_title || ''}
                    fullWidth
                    multiline
                    minRows={2}
                    label="報告標題（顯示於報告抬頭，可多行）"
                    helperText='可換行；報告會自動換行顯示。下方固定為 "(Contract Ref.: 合約編號)"'
                    onChange={handleChangeReportTitle}
                />
            </Grid>

            <FormControl>
                <FormLabel>生效</FormLabel>
                <RadioGroup
                    row
                    value={Contract.is_active?.toString()}
                    onChange={handleChangeIsActive}
                >
                    <FormControlLabel value="1" control={<Radio />} label="生效" />
                    <FormControlLabel value="0" control={<Radio />} label="註銷" />
                </RadioGroup>
            </FormControl>

            <Grid container alignItems="center" style={{ gap: 16 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={Contract.need_inspect === '1'}
                            onChange={(e) => dispatch(handleChangeContractIsInspectionAction(e.target.checked ? '1' : '0'))}
                            color="primary"
                        />
                    }
                    label="包含在巡查"
                />
                <Typography variant="body2" color="textSecondary">
                    開啟後，巡查 App 將會顯示此合約下的地點
                </Typography>
            </Grid>

            {/* Image Type Section */}
            <Grid>
                <FormLabel>相片類型</FormLabel>
                <Typography
                    variant="body2"
                    color={imageTypeList.length >= MAX_IMAGE_TYPE_SELECTION ? 'error' : 'textSecondary'}
                    style={{ marginTop: 6 }}
                >
                    最多 {MAX_IMAGE_TYPE_SELECTION} 種（目前 {imageTypeList.length} 種）
                    {isEditing && ' · 停用的類型不會在清潔 App 顯示，但不影響報告和歷史記錄'}
                </Typography>

                {/* Add new image type form */}
                {imageTypeList.length < MAX_IMAGE_TYPE_SELECTION && (
                    <Paper variant="outlined" style={{ marginTop: 12, padding: 12 }}>
                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs={3}>
                                <TextField
                                    size="small"
                                    label="中文名稱"
                                    value={newTypeName}
                                    onChange={(e) => setNewTypeName(e.target.value)}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    size="small"
                                    label="英文名稱"
                                    value={newTypeNameEn}
                                    onChange={(e) => setNewTypeNameEn(sanitizeImageTypeNameEn(e.target.value))}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    size="small"
                                    label="報告欄位名稱"
                                    value={newTypeReportColumn}
                                    onChange={(e) => setNewTypeReportColumn(e.target.value)}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    disabled={!newTypeName.trim() || !newTypeNameEn.trim() || !newTypeReportColumn.trim()}
                                    onClick={handleAddImageType}
                                >
                                    新增
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>
                )}

                {/* Image type DataGrid */}
                <div style={{ height: imageTypeList.length > 0 ? Math.max(imageTypeList.length * 52 + 56, 150) : 150, width: '100%', marginTop: 8 }}>
                    <DataGrid
                        rows={imageTypeRows}
                        columns={imageTypeColumns}
                        pageSizeOptions={[10, 25]}
                        getRowId={(row) => row.id}
                        disableRowSelectionOnClick
                        localeText={{ noRowsLabel: '尚未新增相片類型' }}
                        rowHeight={44}
                    />
                </div>
            </Grid>

            {Contract.contract_id && (
                <Grid>
                    <Divider style={{ marginBottom: 16 }} />
                    <FormLabel style={{ marginBottom: 8, display: 'block' }}>
                        區份聯絡資訊（顯示於報告抬頭）
                    </FormLabel>
                    <div style={{ height: zoneListLength > 0 ? 400 : 200, width: '100%', marginTop: 8 }}>
                        <DataGrid
                            rows={zoneRows}
                            columns={zoneColumns}
                            pageSizeOptions={[10, 25]}
                            getRowId={(row) => row.id}
                            disableRowSelectionOnClick
                            localeText={{ noRowsLabel: '此合約尚未有區份' }}
                        />
                    </div>
                </Grid>
            )}

            <Dialog
                open={openZoneDetail}
                onClose={handleCloseZoneDetail}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    區份詳情{editingZone ? `：${editingZone.zone_name}` : ''}
                </DialogTitle>
                <DialogContent>
                    {editingZone && (
                        <Grid container spacing={2} style={{ marginTop: 4 }}>
                            {/* Contract selector for changing zone ownership */}
                            {isEditing && editingZone.zone_id && (
                                <Grid item xs={12}>
                                    <Divider style={{ marginBottom: 12 }} />
                                    <Typography variant="subtitle2" color="textSecondary" style={{ marginBottom: 8 }}>
                                        歸屬合約（更改後，區份及底下所有廁所將搬移至新合約）
                                    </Typography>
                                    <Grid container spacing={1} alignItems="center">
                                        <Grid item xs={8}>
                                            <TextField
                                                select
                                                fullWidth
                                                size="small"
                                                label="選擇合約"
                                                value={selectedNewContractId}
                                                onChange={(e) => setSelectedNewContractId(e.target.value)}
                                            >
                                                {(contractList || []).map((c) => (
                                                    <MenuItem key={c.contract_id} value={c.contract_id.toString()}>
                                                        {c.contract}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                        <Grid item>
                                            <Button
                                                variant="contained"
                                                color="warning"
                                                size="small"
                                                startIcon={<SwapHorizIcon />}
                                                disabled={!contractChanged}
                                                onClick={handleMoveZoneToContract}
                                            >
                                                搬移至所選合約
                                            </Button>
                                        </Grid>
                                    </Grid>
                                    {contractChanged && (
                                        <Typography variant="caption" color="warning.main" style={{ marginTop: 4, display: 'block' }}>
                                            注意：此操作會將區份及底下 {zoneBinList.length} 個廁所一起搬移到新合約
                                        </Typography>
                                    )}
                                    <Divider style={{ marginTop: 12, marginBottom: 8 }} />
                                </Grid>
                            )}

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="區份名稱"
                                    value={editingZone.zone_name || ''}
                                    onChange={(e) => handleDraftZoneField('zone_name', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="報告區份名稱"
                                    value={editingZone.report_zone_name || ''}
                                    onChange={(e) => handleDraftZoneField('report_zone_name', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="聯絡人 (Contact Person)"
                                    value={editingZone.contact_person || ''}
                                    onChange={(e) => handleDraftZoneField('contact_person', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="電話 (Tel)"
                                    value={editingZone.tel || ''}
                                    onChange={(e) => handleDraftZoneField('tel', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="電郵 (Email)"
                                    value={editingZone.email || ''}
                                    onChange={(e) => handleDraftZoneField('email', e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Divider style={{ margin: '12px 0' }} />
                                <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                                    廁所列表（共 {zoneBinList.length} 個）
                                </Typography>
                                <div style={{ height: 300, width: '100%' }}>
                                    <DataGrid
                                        rows={binRows}
                                        columns={binColumns}
                                        pageSizeOptions={[10, 25]}
                                        getRowId={(row) => row.id}
                                        disableRowSelectionOnClick
                                    />
                                </div>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseZoneDetail}>
                        <ButtonTextGrid>取消</ButtonTextGrid>
                    </Button>
                    <Button onClick={handleConfirmZoneDetail}>
                        <ButtonTextGrid>確定</ButtonTextGrid>
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={moveZoneSnackbar.open}
                autoHideDuration={4000}
                onClose={() => setMoveZoneSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setMoveZoneSnackbar(prev => ({ ...prev, open: false }))}
                    severity={moveZoneSnackbar.severity}
                    variant="filled"
                >
                    {moveZoneSnackbar.message}
                </Alert>
            </Snackbar>
        </Grid>
    )
}

export default NewContractForm
